import { useEffect, useState } from 'react';
import axios from 'axios';
import { Zap, Plus, Edit, Trash2, Power, PowerOff, Crown, Star, Coins } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/ai-management`;

const MODEL_TYPES = [
  { value: 'reasoning', label: 'Reasoning' },
  { value: 'image_generation', label: 'Image' },
  { value: 'video_generation', label: 'Video' },
  { value: 'audio_generation', label: 'Audio' },
  { value: 'avatar_generation', label: 'Avatar' },
  { value: 'voice_change', label: 'Voice Change' },
];

const TIER_CONFIG = {
  economy: { label: 'Economy', icon: Coins, color: 'emerald', desc: 'Cost-effective' },
  standard: { label: 'Standard', icon: Star, color: 'blue', desc: 'Balanced quality & cost' },
  premium: { label: 'Premium', icon: Crown, color: 'amber', desc: 'Highest quality' },
};

export default function Capabilities() {
  const [capabilities, setCapabilities] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCap, setEditingCap] = useState(null);
  const [formData, setFormData] = useState({
    capability_name: '',
    description: '',
    supported_model_types: [],
    preferred_models: [],
    minimum_quality_score: 5,
    model_tiers: { economy: [], standard: [], premium: [] },
  });

  useEffect(() => {
    fetchModels();
    fetchCapabilities();
  }, []);

  const fetchModels = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/models`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setModels(response.data.filter(m => m.status === 'active'));
    } catch (error) {
      console.error('Error fetching models:', error);
    }
  };

  const fetchCapabilities = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/capabilities`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCapabilities(response.data);
    } catch (error) {
      console.error('Error fetching capabilities:', error);
      toast.error('Failed to fetch capabilities');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      const payload = {
        ...formData,
        model_tiers: formData.model_tiers || { economy: [], standard: [], premium: [] },
      };

      if (editingCap) {
        await axios.put(
          `${API}/capabilities/${editingCap.capability_id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Capability updated');
      } else {
        await axios.post(`${API}/capabilities`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Capability created');
      }

      setShowDialog(false);
      setEditingCap(null);
      resetForm();
      fetchCapabilities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save capability');
    }
  };

  const handleEdit = (cap) => {
    setEditingCap(cap);
    setFormData({
      capability_name: cap.capability_name,
      description: cap.description,
      supported_model_types: cap.supported_model_types,
      preferred_models: cap.preferred_models,
      minimum_quality_score: cap.minimum_quality_score,
      model_tiers: cap.model_tiers || { economy: [], standard: [], premium: [] },
    });
    setShowDialog(true);
  };

  const handleDelete = async (capId) => {
    if (!window.confirm('Delete this capability?')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/capabilities/${capId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Capability deleted');
      fetchCapabilities();
    } catch (error) {
      toast.error('Failed to delete capability');
    }
  };

  const toggleActive = async (cap) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/capabilities/${cap.capability_id}`,
        { is_active: !cap.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Capability ${!cap.is_active ? 'enabled' : 'disabled'}`);
      fetchCapabilities();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const resetForm = () => {
    setFormData({
      capability_name: '',
      description: '',
      supported_model_types: [],
      preferred_models: [],
      minimum_quality_score: 5,
      model_tiers: { economy: [], standard: [], premium: [] },
    });
  };

  const handleTypeToggle = (type) => {
    setFormData(prev => ({
      ...prev,
      supported_model_types: prev.supported_model_types.includes(type)
        ? prev.supported_model_types.filter(t => t !== type)
        : [...prev.supported_model_types, type]
    }));
  };

  const handleModelToggle = (modelId) => {
    setFormData(prev => ({
      ...prev,
      preferred_models: prev.preferred_models.includes(modelId)
        ? prev.preferred_models.filter(m => m !== modelId)
        : [...prev.preferred_models, modelId]
    }));
  };

  const handleTierToggle = (tier, modelId) => {
    setFormData(prev => {
      const currentTier = prev.model_tiers?.[tier] || [];
      const newTier = currentTier.includes(modelId)
        ? currentTier.filter(m => m !== modelId)
        : [...currentTier, modelId];
      return {
        ...prev,
        model_tiers: { ...prev.model_tiers, [tier]: newTier },
      };
    });
  };

  const getModelName = (modelId) => {
    const m = models.find(m => m.model_id === modelId);
    return m ? `${m.model_name} (${m.provider_name})` : modelId;
  };

  const hasAnyTier = (tiers) => {
    if (!tiers) return false;
    return (tiers.economy?.length > 0 || tiers.standard?.length > 0 || tiers.premium?.length > 0);
  };

  const getTierSummary = (cap) => {
    const tiers = cap.model_tiers;
    if (!tiers) return [];
    const result = [];
    if (tiers.economy?.length > 0) result.push({ key: 'economy', count: tiers.economy.length });
    if (tiers.standard?.length > 0) result.push({ key: 'standard', count: tiers.standard.length });
    if (tiers.premium?.length > 0) result.push({ key: 'premium', count: tiers.premium.length });
    return result;
  };

  // Only show preferred_models that are selected for tier assignment
  const selectedModels = formData.preferred_models;

  if (loading) return <div className="flex items-center justify-center h-full"><div className="text-lg text-slate-600">Loading...</div></div>;

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-2" data-testid="capabilities-title">Capabilities</h1>
          <p className="text-slate-600">Define abstract capabilities that map to AI models</p>
        </div>
        <Button data-testid="add-capability-btn" onClick={() => { resetForm(); setEditingCap(null); setShowDialog(true); }} className="bg-purple-600 hover:bg-purple-700">
          <Plus className="w-4 h-4 mr-2" /> Add Capability
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {capabilities.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-white rounded-lg shadow-md">
            <Zap className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            <p className="text-slate-500 mb-4">No capabilities defined yet</p>
            <Button onClick={() => setShowDialog(true)} className="bg-purple-600 hover:bg-purple-700">
              <Plus className="w-4 h-4 mr-2" /> Create First Capability
            </Button>
          </div>
        ) : (
          capabilities.map((cap) => {
            const tierSummary = getTierSummary(cap);
            return (
              <div key={cap.capability_id} data-testid={`capability-card-${cap.capability_id}`} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center">
                    <Zap className="w-7 h-7 text-purple-600 mr-3" />
                    <div>
                      <h3 className="font-semibold text-lg text-slate-800">{cap.capability_name}</h3>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 ${cap.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {cap.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <button onClick={() => toggleActive(cap)} className={`p-2 rounded-lg ${cap.is_active ? 'text-green-600 hover:bg-green-50' : 'text-gray-600 hover:bg-gray-50'}`}>
                    {cap.is_active ? <Power className="w-5 h-5" /> : <PowerOff className="w-5 h-5" />}
                  </button>
                </div>

                <p className="text-sm text-slate-600 mb-4 line-clamp-2">{cap.description}</p>

                <div className="mb-3">
                  <div className="text-xs text-slate-500 mb-1.5">Supported Types:</div>
                  <div className="flex flex-wrap gap-1">
                    {cap.supported_model_types.map((type) => (
                      <span key={type} className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">{type}</span>
                    ))}
                  </div>
                </div>

                {tierSummary.length > 0 && (
                  <div className="mb-3" data-testid={`tier-badges-${cap.capability_id}`}>
                    <div className="text-xs text-slate-500 mb-1.5">Model Tiers:</div>
                    <div className="flex flex-wrap gap-1.5">
                      {tierSummary.map(({ key, count }) => {
                        const cfg = TIER_CONFIG[key];
                        const Icon = cfg.icon;
                        return (
                          <span key={key} className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-${cfg.color}-100 text-${cfg.color}-800`}>
                            <Icon className="w-3 h-3" />
                            {cfg.label} ({count})
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="mb-4 text-xs text-slate-500">
                  <div>Min Quality: {cap.minimum_quality_score}/10</div>
                  <div>Preferred Models: {cap.preferred_models.length}</div>
                </div>

                <div className="flex space-x-2">
                  <button data-testid={`edit-cap-${cap.capability_id}`} onClick={() => handleEdit(cap)} className="flex-1 flex items-center justify-center px-3 py-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 text-sm">
                    <Edit className="w-4 h-4 mr-1" /> Edit
                  </button>
                  <button onClick={() => handleDelete(cap.capability_id)} className="flex-1 flex items-center justify-center px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 text-sm">
                    <Trash2 className="w-4 h-4 mr-1" /> Delete
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCap ? 'Edit Capability' : 'New Capability'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-5 py-4">
              <div>
                <Label>Capability Name *</Label>
                <Input
                  data-testid="cap-name-input"
                  placeholder="e.g., cinematic_video, ultra_realistic_image"
                  value={formData.capability_name}
                  onChange={(e) => setFormData({...formData, capability_name: e.target.value})}
                  required
                />
              </div>

              <div>
                <Label>Description *</Label>
                <Textarea
                  placeholder="What this capability does"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  required
                />
              </div>

              <div>
                <Label>Supported Model Types *</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {MODEL_TYPES.map((type) => (
                    <label key={type.value} className="flex items-center space-x-2 p-2 border rounded cursor-pointer hover:bg-slate-50">
                      <input type="checkbox" checked={formData.supported_model_types.includes(type.value)} onChange={() => handleTypeToggle(type.value)} />
                      <span className="text-sm">{type.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label>Preferred Models (in order)</Label>
                <div className="max-h-36 overflow-y-auto border rounded p-2 space-y-1">
                  {models.map((model) => (
                    <label key={model.model_id} className="flex items-center space-x-2 p-1 hover:bg-slate-50 rounded cursor-pointer">
                      <input type="checkbox" checked={formData.preferred_models.includes(model.model_id)} onChange={() => handleModelToggle(model.model_id)} />
                      <span className="text-sm">{model.model_name} <span className="text-slate-400">({model.provider_name})</span></span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label>Minimum Quality Score (1-10)</Label>
                <Input type="number" min="1" max="10" value={formData.minimum_quality_score} onChange={(e) => setFormData({...formData, minimum_quality_score: parseInt(e.target.value)})} />
              </div>

              {/* Model Tiers Section */}
              {selectedModels.length > 0 && (
                <div className="border-t pt-4" data-testid="tier-assignment-section">
                  <Label className="text-base font-semibold mb-1 block">Model Tiers (Optional)</Label>
                  <p className="text-xs text-slate-500 mb-3">Assign preferred models to Economy, Standard, or Premium tiers for user selection. Only tiers with models assigned will be shown to users.</p>

                  <div className="space-y-3">
                    {Object.entries(TIER_CONFIG).map(([tierKey, cfg]) => {
                      const Icon = cfg.icon;
                      const tierModels = formData.model_tiers?.[tierKey] || [];
                      return (
                        <div key={tierKey} className="border rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Icon className={`w-4 h-4 text-${cfg.color}-600`} />
                            <span className="font-medium text-sm">{cfg.label}</span>
                            <span className="text-xs text-slate-400">{cfg.desc}</span>
                            {tierModels.length > 0 && (
                              <span className={`ml-auto text-xs font-medium px-2 py-0.5 rounded-full bg-${cfg.color}-100 text-${cfg.color}-700`}>
                                {tierModels.length} model{tierModels.length > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          <div className="space-y-1 max-h-28 overflow-y-auto">
                            {selectedModels.map((modelId) => (
                              <label key={modelId} className="flex items-center space-x-2 p-1 hover:bg-slate-50 rounded cursor-pointer text-sm">
                                <input
                                  type="checkbox"
                                  data-testid={`tier-${tierKey}-${modelId}`}
                                  checked={tierModels.includes(modelId)}
                                  onChange={() => handleTierToggle(tierKey, modelId)}
                                />
                                <span>{getModelName(modelId)}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowDialog(false); setEditingCap(null); resetForm(); }}>Cancel</Button>
              <Button data-testid="save-capability-btn" type="submit" className="bg-purple-600 hover:bg-purple-700">{editingCap ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
